import { useState, useCallback, useEffect } from 'react';

export type AIGenerationType = 'package_description' | 'proposal_message' | 'package_benefits';

export interface PackageContext {
  title: string;
  price?: string;
  currency?: string;
  benefits?: string[];
  icp?: string;
  language?: string;
}

export interface ProposalContext {
  clientName: string;
  packageTitle?: string;
  eventDate?: string;
  tenantName?: string;
  icp?: string;
  language?: string;
}

export interface GeneratedBenefit {
  icon: 'clock' | 'shield' | 'sparkle' | 'trophy' | 'star';
  text: string;
}

export interface AIUsage {
  used: number;
  limit: number;
  resetsAt: string | null;
}

export interface GenerateResponse {
  text: string;
  fromCache: boolean;
  usage: AIUsage;
}

function getCsrfToken(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const csrfPair = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('csrf_token='))

  if (!csrfPair) {
    return null
  }

  return decodeURIComponent(csrfPair.substring('csrf_token='.length))
}

interface GenerateParams {
  type: AIGenerationType;
  context: PackageContext | ProposalContext;
  skipCache?: boolean;
}

async function generateWithAI(params: GenerateParams): Promise<GenerateResponse> {
  const csrfToken = getCsrfToken()
  const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:4000'}/api/ai/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `Error ${response.status}`);
  }

  return response.json();
}

async function getAIUsage(): Promise<AIUsage> {
  const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:4000'}/api/ai/usage`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `Error ${response.status}`);
  }

  return response.json();
}

export function useAIGenerate() {
  const [lastResult, setLastResult] = useState<GenerateResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [usage, setUsage] = useState<AIUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // Fetch usage on mount
  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const data = await getAIUsage();
      setUsage(data);
    } catch (err) {
      console.warn('Failed to fetch AI usage:', err);
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const generate = useCallback(
    async (type: AIGenerationType, context: PackageContext | ProposalContext) => {
      setIsGenerating(true);
      setError(null);
      try {
        const result = await generateWithAI({ type, context, skipCache: false });
        setLastResult(result);
        setUsage(result.usage);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        throw error;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  const regenerate = useCallback(
    async (type: AIGenerationType, context: PackageContext | ProposalContext) => {
      setIsGenerating(true);
      setError(null);
      try {
        const result = await generateWithAI({ type, context, skipCache: true });
        setLastResult(result);
        setUsage(result.usage);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        throw error;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setLastResult(null);
    setError(null);
  }, []);

  const currentUsage = usage || lastResult?.usage;

  return {
    // Generation
    generate,
    regenerate,
    reset,
    
    // State
    isGenerating,
    data: lastResult,
    error,
    
    // Usage
    usage: currentUsage,
    usageLoading,
    refetchUsage: fetchUsage,
    
    // Computed
    hasReachedLimit: currentUsage ? currentUsage.used >= currentUsage.limit : false,
    // During loading (currentUsage is null/undefined), return true to avoid blocking Studio users
    hasAccess: currentUsage ? currentUsage.limit > 0 : true,
    remainingGenerations: currentUsage ? Math.max(0, currentUsage.limit - currentUsage.used) : undefined,
    isLoadingAccess: usageLoading || !currentUsage,
  };
}

export function useAIUsage() {
  const [usage, setUsage] = useState<AIUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAIUsage();
      setUsage(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return {
    data: usage,
    isLoading: loading,
    error,
    refetch: fetchUsage,
  };
}
